import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, AlertCircle, X, UserPlus } from 'lucide-react';
import { CustomerStatus } from '../../types/crm';
import { API_ENDPOINTS } from '../../config/api';

interface CustomerStatusDropdownProps {
  status: CustomerStatus;
  isOverride: boolean;
  petOwnerId: string;
  petOwnerName: string;
  onStatusUpdate?: (newStatus: CustomerStatus) => void;
  canEdit?: boolean;
  className?: string;
}

const getStatusConfig = (status: CustomerStatus) => {
  const configs = {
    PROSPECT: {
      label: 'Prospect',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      icon: UserPlus
    },
    OPEN: {
      label: 'Active',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-200',
      icon: Check
    },
    COLD: {
      label: 'Needs Appt',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-200',
      icon: AlertCircle
    },
    LOST: {
      label: 'Lost',
      bgColor: 'bg-red-100',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      icon: X
    }
  };
  return configs[status];
};

const ALL_STATUSES: CustomerStatus[] = ['PROSPECT', 'OPEN', 'COLD', 'LOST'];

const CustomerStatusDropdown: React.FC<CustomerStatusDropdownProps> = ({
  status,
  isOverride,
  petOwnerId,
  petOwnerName,
  onStatusUpdate,
  canEdit = true,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const config = getStatusConfig(status);
  const Icon = config.icon;

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownWidth = 192; // w-48 = 192px
      
      // Estimate dropdown height (3 items * ~40px each)
      const estimatedHeight = 120;
      
      // Determine if should open upward
      const shouldOpenUpward = spaceBelow < estimatedHeight + 20 && rect.top > estimatedHeight + 20;
      
      // Calculate position
      const top = shouldOpenUpward 
        ? rect.top - 8  // Position above button with small margin
        : rect.bottom + 8;
      
      // Align to right edge of button, but keep within viewport
      const left = Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8));
      
      setDropdownPosition({ top, left, openUpward: shouldOpenUpward });
    }
  }, [isOpen]);

  const handleStatusClick = async (newStatus: CustomerStatus) => {
    if (!canEdit || isUpdating || newStatus === status) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(API_ENDPOINTS.PET_OWNER_CRM.UPDATE_STATUS(petOwnerId), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          override: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      if (onStatusUpdate) {
        onStatusUpdate(newStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };

  if (!canEdit) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium border rounded-full ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{config.label}</span>
      </span>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isUpdating}
        className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium border rounded-full transition-colors ${
          config.bgColor
        } ${config.textColor} ${config.borderColor} hover:opacity-80 ${
          isOverride ? 'ring-2 ring-blue-400 ring-offset-1' : ''
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{config.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            ref={dropdownRef}
            className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 flex flex-col"
            style={{
              [dropdownPosition.openUpward ? 'bottom' : 'top']: dropdownPosition.openUpward 
                ? `${window.innerHeight - dropdownPosition.top}px`
                : `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {ALL_STATUSES.map((statusOption) => {
              const optionConfig = getStatusConfig(statusOption);
              const OptionIcon = optionConfig.icon;
              const isSelected = statusOption === status;
              
              return (
                <button
                  key={statusOption}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusClick(statusOption);
                  }}
                  disabled={isUpdating}
                  className="w-full px-3 py-2 hover:bg-blue-50 transition-colors text-left disabled:opacity-50 block"
                >
                  <div className="flex items-center gap-2">
                    <OptionIcon className={`w-4 h-4 ${optionConfig.textColor}`} />
                    <span className="text-sm font-medium text-gray-900">
                      {optionConfig.label}
                    </span>
                    {isSelected && (
                      <Check className="w-4 h-4 ml-auto text-blue-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default CustomerStatusDropdown;

